import { EyeOpenIcon, Pencil1Icon } from "@radix-ui/react-icons";
import { ColumnDef } from "@tanstack/react-table";
import dayjs from "dayjs";
import { useMemo, useState } from "react";

import {
  ICompletedSurveyResponse,
  IWardSurveyDetail,
  IWardSurveyOverview,
} from "../../../@types/admin/AdminSurvey";
import { TableSorted } from "../../../components/TableSorted";
import { useFileDownload } from "../../../hooks/mutations/admin/fileTransfer";
import {
  useCompletedResponses,
  useSurveyAnswerAuditLogs,
  useWardSurveyDetails,
} from "../../../hooks/queries/admin/surveys";
import { surveyExportRequests } from "../../../services/admin/surveysService";
import { getLongFormattedDate } from "../../../utils/dateFormatter";
import {
  AdminBreadcrumb,
  AuditLogPanelFilters,
  AuditLogTable,
  auditWindowError,
  auditWindowToRange,
  IAuditLogWindow,
  latestAuditWindow,
  ErrorMessage,
  FilterButton,
  IAdminBreadcrumbItem,
  ModalContainer,
  ModalHeader,
  ModalSubtitle,
  ModalTitle,
  RowAction,
  TableContainer,
  TableLoadingState,
  TablePagination,
  TableWrapper,
  useMockPagination,
} from "../shared";
import { RowActions } from "./AdminSurveys.styles";
import { SurveyAnswerEditor } from "./SurveyAnswerEditor";

interface IWardSurveyDrillDownModalProps {
  ward: IWardSurveyOverview;
}

type IDrillLevel = "surveys" | "responses" | "answer";

/**
 * One ward's surveys, one survey's responses, and one response's answers and
 * history — all in a single modal (EN-5164).
 *
 * Retool used four separate modal frames for this
 * (`wardSurveys_modalFrame` → `completedResponses_modalFrame` →
 * `editSurveyAnswers_modalFrame` → `surveyAnswersHistory_modalFrame`, plus
 * `modalSurveyAnswerHistoryDetail`). The portal has one global modal slot, so
 * per the EN-5149 rule this walks its own levels behind a breadcrumb.
 */
export function WardSurveyDrillDownModal({
  ward,
}: IWardSurveyDrillDownModalProps) {
  const { downloadFile, isDownloading } = useFileDownload();

  const [level, setLevel] = useState<IDrillLevel>("surveys");
  const [survey, setSurvey] = useState<IWardSurveyDetail | null>(null);
  const [response, setResponse] = useState<ICompletedSurveyResponse | null>(
    null
  );
  const [showHistory, setShowHistory] = useState(false);
  const [isEditorDirty, setIsEditorDirty] = useState(false);

  const { wardSurveyDetails, isLoading, isError } = useWardSurveyDetails(
    ward.wardId
  );
  const { completedResponses, isLoading: responsesLoading } =
    useCompletedResponses(
      level === "responses" || level === "answer"
        ? survey?.surveyId ?? undefined
        : undefined
    );

  // Answer history: one window of at most 90 days, steppable back (EN-5288).
  const [auditWindow, setAuditWindow] =
    useState<IAuditLogWindow>(latestAuditWindow);
  const auditRange = useMemo(
    () => auditWindowToRange(auditWindow),
    [auditWindow]
  );
  const [auditPage, setAuditPage] = useState(1);
  const [auditPageSize, setAuditPageSize] = useState(10);
  const {
    auditLogs,
    pagination: auditPagination,
    isLoading: auditLoading,
    isError: auditError,
  } = useSurveyAnswerAuditLogs(
    showHistory && !auditWindowError(auditWindow)
      ? response?.surveyAnswerId
      : undefined,
    auditRange.startDate,
    auditRange.endDate,
    auditPage,
    auditPageSize
  );

  const surveyPagination = useMockPagination({
    data: wardSurveyDetails,
    searchFields: ["surveyName", "source"],
  });
  const responsePagination = useMockPagination({
    data: completedResponses,
    searchFields: ["patientName", "patientEmail"],
  });

  const goToSurveys = () => {
    setLevel("surveys");
    setSurvey(null);
    setResponse(null);
    setShowHistory(false);
  };

  const goToResponses = () => {
    setLevel("responses");
    setResponse(null);
    setShowHistory(false);
  };

  const surveyColumns: ColumnDef<IWardSurveyDetail, string>[] = useMemo(
    () => [
      { accessorKey: "surveyName", header: "Survey", enableSorting: true },
      { accessorKey: "source", header: "Source", enableSorting: true },
      {
        id: "completedSurveyCount",
        header: "Completed surveys",
        accessorFn: (row) => String(row.completedSurveyCount ?? 0),
        enableSorting: true,
      },
      {
        id: "completedPatientCount",
        header: "Patients",
        accessorFn: (row) => String(row.completedPatientCount ?? 0),
        enableSorting: true,
      },
      {
        id: "actions",
        header: "",
        enableSorting: false,
        cell: ({ row }) => (
          <RowActions>
            <RowAction
              label="View responses"
              icon={<EyeOpenIcon />}
              disabled={
                !row.original.surveyId ||
                row.original.completedSurveyCount === 0
              }
              onClick={() => {
                setSurvey(row.original);
                setLevel("responses");
              }}
            />
          </RowActions>
        ),
      },
    ],
    []
  );

  /**
   * The answer columns vary per survey — `CompletedSurveyResponseDto` carries
   * `[JsonExtensionData]`, so each row holds one extra key per question. They
   * are discovered from the data, as Retool's transformer did.
   */
  const answerKeys = useMemo(() => {
    const known = new Set([
      "surveyAnswerId",
      "patientId",
      "patientName",
      "patientEmail",
      "createdDate",
      "totalScore",
      "domainScores",
    ]);
    const keys = new Set<string>();
    completedResponses.forEach((row) => {
      Object.keys(row).forEach((key) => {
        if (!known.has(key)) keys.add(key);
      });
    });
    return Array.from(keys);
  }, [completedResponses]);

  const responseColumns: ColumnDef<ICompletedSurveyResponse, string>[] =
    useMemo(
      () => [
        {
          id: "patientId",
          header: "Patient ID",
          accessorFn: (row) => String(row.patientId ?? ""),
          enableSorting: true,
        },
        { accessorKey: "patientName", header: "Patient", enableSorting: true },
        {
          accessorKey: "createdDate",
          header: "Completed",
          cell: ({ row }) => (
            <span>{getLongFormattedDate(row.original.createdDate, "-")}</span>
          ),
          enableSorting: true,
        },
        {
          id: "totalScore",
          header: "Total score",
          accessorFn: (row) =>
            row.totalScore == null ? "-" : String(row.totalScore),
          enableSorting: true,
        },
        // Retool flattened domainScores into one column per domain; the same
        // shape, built per row so a survey without domains shows nothing.
        {
          id: "domainScores",
          header: "Domain scores",
          accessorFn: (row) =>
            (row.domainScores ?? [])
              .map((domain) => `${domain.domain}: ${domain.score}`)
              .join(", "),
          enableSorting: true,
        },
        ...answerKeys.map<ColumnDef<ICompletedSurveyResponse, string>>(
          (key) => ({
            id: `answer-${key}`,
            header: key,
            accessorFn: (row) => {
              const value = row[key];
              return value == null ? "" : String(value);
            },
            enableSorting: true,
          })
        ),
        {
          id: "actions",
          header: "",
          enableSorting: false,
          cell: ({ row }) => (
            <RowActions>
              <RowAction
                label="Edit answers"
                icon={<Pencil1Icon />}
                onClick={() => {
                  setResponse(row.original);
                  setShowHistory(false);
                  setAuditPage(1);
                  setIsEditorDirty(false);
                  setLevel("answer");
                }}
              />
            </RowActions>
          ),
        },
      ],
      [answerKeys]
    );

  const breadcrumb: IAdminBreadcrumbItem[] = [
    { label: ward.wardName ?? `Ward ${ward.wardId}`, onClick: goToSurveys },
  ];
  if (level !== "surveys" && survey) {
    breadcrumb.push({
      label: survey.surveyName ?? "Survey",
      onClick: level === "answer" ? goToResponses : undefined,
    });
  }
  if (level === "answer" && response) {
    breadcrumb.push({
      label: response.patientName ?? `Patient ${response.patientId}`,
    });
  }

  const handleExportSurveys = () =>
    downloadFile(surveyExportRequests.wardSurveyDetails(ward.wardId), {
      fallbackFilename: "WardSurveyDetails.xlsx",
    });

  const handleExportResponses = () => {
    if (!survey?.surveyId) return;
    downloadFile(surveyExportRequests.completedResponses(survey.surveyId), {
      fallbackFilename: `${ward.wardName ?? "Ward"} ${
        survey.surveyName ?? "Survey"
      } ${dayjs().format("DD-MM-YYYY")}.xlsx`,
    });
  };

  return (
    <ModalContainer>
      <ModalHeader>
        <ModalTitle>
          {level === "answer" ? "Edit survey answers" : "Ward surveys"}
        </ModalTitle>
        <AdminBreadcrumb items={breadcrumb} ariaLabel="Survey drill-down" />
        <ModalSubtitle>
          {ward.hospitalName ?? "Unknown hospital"} · ward {ward.wardId}
        </ModalSubtitle>
      </ModalHeader>

      {isError && level === "surveys" && (
        <ErrorMessage>
          Failed to load this ward&apos;s surveys. Close the dialog and try
          again.
        </ErrorMessage>
      )}

      {level === "surveys" && (
        <>
          <RowActions>
            <FilterButton
              variant="secondary"
              disabled={isDownloading}
              onClick={handleExportSurveys}
            >
              {isDownloading ? "Exporting..." : "Export ward surveys"}
            </FilterButton>
          </RowActions>
          <TableContainer>
            <TableWrapper refreshKeys={[["admin-ward-survey-details"]]}>
              <TableSorted
                columns={surveyColumns}
                data={surveyPagination.items}
                emptyStateText={
                  isLoading ? <TableLoadingState /> : "No rows found"
                }
              />
            </TableWrapper>
            <TablePagination
              pagination={surveyPagination.pagination}
              onPageChange={surveyPagination.setPage}
              onPageSizeChange={surveyPagination.setPageSize}
              exportCsv={{
                filename: `ward-${ward.wardId}-surveys`,
                columns: surveyColumns,
                rows: surveyPagination.allItems,
              }}
            />
          </TableContainer>
        </>
      )}

      {level === "responses" && (
        <>
          <RowActions>
            <FilterButton
              variant="secondary"
              disabled={isDownloading}
              onClick={handleExportResponses}
            >
              {isDownloading ? "Exporting..." : "Export responses"}
            </FilterButton>
          </RowActions>
          <TableContainer>
            <TableWrapper refreshKeys={[["admin-completed-responses"]]}>
              <TableSorted
                columns={responseColumns}
                data={responsePagination.items}
                emptyStateText={
                  responsesLoading ? <TableLoadingState /> : "No rows found"
                }
              />
            </TableWrapper>
            <TablePagination
              pagination={responsePagination.pagination}
              onPageChange={responsePagination.setPage}
              onPageSizeChange={responsePagination.setPageSize}
              exportCsv={{
                filename: `ward-${ward.wardId}-survey-responses`,
                columns: responseColumns,
                rows: responsePagination.allItems,
              }}
            />
          </TableContainer>
        </>
      )}

      {level === "answer" && response && (
        <>
          <RowActions>
            <FilterButton
              variant="secondary"
              onClick={() => setShowHistory((previous) => !previous)}
            >
              {showHistory ? "Back to answers" : "View history"}
            </FilterButton>
          </RowActions>

          {showHistory && (
            <AuditLogTable
              logs={auditLogs}
              pagination={auditPagination}
              onPageChange={setAuditPage}
              onPageSizeChange={(size) => {
                setAuditPageSize(size);
                setAuditPage(1);
              }}
              isLoading={auditLoading}
              isError={auditError}
              title="Answer history"
              filters={
                <AuditLogPanelFilters
                  idPrefix="survey-answer-audit"
                  auditWindow={auditWindow}
                  onWindowChange={(next) => {
                    setAuditWindow(next);
                    setAuditPage(1);
                  }}
                />
              }
              refreshKeys={[["admin-survey-answer-audit"]]}
            />
          )}

          {/* Hidden rather than unmounted while the history shows: the
              editor holds unsaved edits in its own state, and the banner
              below promises they survive the round trip. */}
          <div style={{ display: showHistory ? "none" : undefined }}>
            <SurveyAnswerEditor
              surveyAnswerId={response.surveyAnswerId}
              onDirtyChange={setIsEditorDirty}
            />
          </div>

          {showHistory && isEditorDirty && (
            <ErrorMessage>
              You have unsaved answer changes — go back to the answers to save
              them.
            </ErrorMessage>
          )}
        </>
      )}
    </ModalContainer>
  );
}
